import { findElementByExternalIdAndSceneType } from "@/lib/stores/editor-store/editor-state";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { ElementDto, SceneTypeDto } from "@repo/api/overlays";
import {
  IconChevronRight,
  IconDots,
  IconEye,
  IconEyeOff,
  IconPhoto,
  IconTrash,
  IconVideo,
} from "@tabler/icons-react";
import IconLetterCaseToggle from "./Icons/IconLetterCaseToggle";
import { AssetMediaAsyncDto } from "@repo/api/asset-media";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { DraggableAttributes } from "@dnd-kit/core";
import { forwardRef } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import { Button } from "@/lib/components/Button/Button";
import { CompositionTimings } from "@repo/video-engine-i2v/utils/timing";
import { getHexWithOpacity } from "@repo/video-engine-i2v/utils/color";
import { cn, needsBorder } from "@/lib/utils";

type Props = {
  elementExternalId: string;
  onClick: (elementExternalId: string, event: React.MouseEvent) => void;
  style?: React.CSSProperties;
  isDragging?: boolean;
  listeners?: SyntheticListenerMap;
  attributes?: DraggableAttributes;
  sceneType: SceneTypeDto;
  compositionTimings: CompositionTimings | null;
};

export const ElementListItem = forwardRef<HTMLDivElement, Props>(
  (
    {
      elementExternalId,
      onClick,
      isDragging,
      listeners,
      attributes,
      style,
      sceneType,
    },
    ref,
  ) => {
    const element = useEditorStore((state) =>
      findElementByExternalIdAndSceneType(state, elementExternalId, sceneType),
    );

    const assetMedia = useEditorStore((state) => {
      if (!element || !("assetMediaExternalId" in element)) {
        return null;
      }

      return state.assetMedias[element.assetMediaExternalId] ?? null;
    });

    const isSelected = useEditorStore((state) =>
      state.selectedElementIds.includes(elementExternalId),
    );

    const toggleElementsVisibility = useEditorStore(
      (state) => state.service.toggleElementsVisibility,
    );

    if (!element) {
      return null;
    }

    const opacity = isDragging ? "opacity-0" : "";

    return (
      <div
        ref={ref}
        className={cn(
          "group flex w-full cursor-pointer items-center rounded-lg border border-transparent py-1 pr-4 pl-1 select-none",
          isSelected
            ? "border-tertiary-400/10 bg-tertiary-50"
            : "hover:bg-grey-100",
          opacity,
        )}
        onClick={(event) => onClick(elementExternalId, event)}
        style={style}
        data-element-id={elementExternalId}
        {...attributes}
        {...listeners}
      >
        <div
          className={cn(
            "relative h-12 w-12 overflow-hidden",
            element.type !== "RECTANGLE" && "rounded-md",
            element.type !== "RECTANGLE" && !element.isHidden && "bg-black-400",
          )}
        >
          <ElementThumbnail
            element={element}
            assetMedia={assetMedia}
            isSelected={isSelected}
          />
          {element.isHidden && (
            <button
              type="button"
              aria-label="Show element"
              onClick={(e) => {
                e.stopPropagation();
                toggleElementsVisibility([element.externalId]);
              }}
              className={cn(
                "absolute inset-0 flex items-center justify-center rounded-md",
                isSelected
                  ? "bg-tertiary-50"
                  : "bg-white group-hover:bg-grey-100",
              )}
            >
              <IconEyeOff size={20} />
            </button>
          )}
        </div>
        <p className="ml-4 line-clamp-1 flex-1 text-sm font-semibold">
          <ElementLabel element={element} />
        </p>

        <ElementOptionsDropdown element={element} sceneType={sceneType} />
        <div className="w-1"></div>
        <IconChevronRight size={20} />
      </div>
    );
  },
);

type ElementThumbnailProps = {
  element: ElementDto;
  assetMedia: AssetMediaAsyncDto | null;
  isSelected: boolean;
};

function ElementThumbnail({
  element,
  assetMedia,
  isSelected,
}: ElementThumbnailProps) {
  if (element.type === "TEXT") {
    return (
      <div
        className={cn(
          "flex h-full w-full items-center justify-center",
          isSelected
            ? "bg-tertiary-50"
            : "bg-linear-to-br from-primary-100 to-secondary-100",
        )}
      >
        <IconLetterCaseToggle
          startColor={
            isSelected
              ? "var(--color-tertiary-400)"
              : "var(--color-primary-400)"
          }
          endColor={
            isSelected
              ? "var(--color-tertiary-400)"
              : "var(--color-secondary-400)"
          }
        />
      </div>
    );
  }

  if (element.type === "IMAGE") {
    if (!assetMedia || assetMedia.status !== "COMPLETED") {
      return (
        <div className="flex h-full w-full items-center justify-center">
          <IconPhoto size={24} className="text-white" />
        </div>
      );
    }

    return (
      <img
        src={assetMedia.data.thumbnailSrc ?? assetMedia.data.src}
        className="h-full w-full object-contain object-center"
      />
    );
  }

  if (element.type === "VIDEO") {
    if (!assetMedia || assetMedia.status !== "COMPLETED") {
      return (
        <div className="flex h-full w-full items-center justify-center">
          <IconVideo size={24} className="text-white" />
        </div>
      );
    }

    if (assetMedia.data.thumbnailSrc) {
      return (
        <img
          src={assetMedia.data.thumbnailSrc}
          className="h-full w-full object-contain object-center"
        />
      );
    }

    return (
      <video
        src={assetMedia.data.src}
        playsInline
        controls={false}
        muted
        preload="auto"
        className="h-full w-full object-contain object-center"
      />
    );
  }

  if (element.type === "RECTANGLE") {
    return (
      <div
        className={cn(
          "h-full w-full rounded-md border",
          needsBorder(
            element.backgroundColor.color,
            element.backgroundColor.opacity ?? 100,
          )
            ? "border-input"
            : "border-transparent",
        )}
        style={{
          backgroundColor: getHexWithOpacity(
            element.backgroundColor.color,
            element.backgroundColor.opacity ?? 100,
          ),
        }}
      />
    );
  }

  return null;
}

function ElementLabel({ element }: { element: ElementDto }) {
  if (element.type === "TEXT") {
    return <span>{element.text || "No text"}</span>;
  }

  if (element.type === "IMAGE") {
    return <span>Image</span>;
  }

  if (element.type === "VIDEO") {
    return <span>Video</span>;
  }

  if (element.type === "RECTANGLE") {
    return <span>Rectangle</span>;
  }

  return null;
}

type ElementOptionsDropdownProps = {
  element: ElementDto;
  sceneType: SceneTypeDto;
};

function ElementOptionsDropdown({
  element,
  sceneType,
}: ElementOptionsDropdownProps) {
  const deleteElement = useEditorStore((state) => state.service.deleteElement);
  const toggleElementsVisibility = useEditorStore(
    (state) => state.service.toggleElementsVisibility,
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <IconDots size={20} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-56"
        align="start"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            toggleElementsVisibility([element.externalId]);
          }}
        >
          <div className="flex items-center gap-2">
            {element.isHidden ? (
              <IconEyeOff size={18} />
            ) : (
              <IconEye size={18} />
            )}
            <span className="text-sm">
              {element.isHidden ? "Show element" : "Hide element"}
            </span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            deleteElement(element.externalId, sceneType);
          }}
        >
          <div className="flex items-center gap-2">
            <IconTrash size={18} />
            <span className="text-sm">Remove element</span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type SortableElementListItemProps = {
  elementExternalId: string;
  onClick: (elementExternalId: string, event: React.MouseEvent) => void;
  sceneType: SceneTypeDto;
  compositionTimings: CompositionTimings | null;
};

export function SortableElementListItem({
  elementExternalId,
  onClick,
  sceneType,
  compositionTimings,
}: SortableElementListItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, active } =
    useSortable({ id: elementExternalId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <ElementListItem
      ref={setNodeRef}
      style={style}
      elementExternalId={elementExternalId}
      isDragging={active?.id === elementExternalId}
      attributes={attributes}
      listeners={listeners}
      onClick={onClick}
      sceneType={sceneType}
      compositionTimings={compositionTimings}
    />
  );
}
